import type { BuildingType, CulturalReviewStatus } from '@aztec-realms/shared-types';

import type { ConstructionStageId } from './construction-stage';
import { SETTLEMENT_CAMERA_PROFILE } from './settlement-projection';
import homeRuntimeArt from './home-runtime-art.json';
import { settlementBuildingRegistration } from './settlement-building-art';

/** Original fantasy object renders; visual identity only, never source or eligibility evidence. */
export const worldNodeAssetManifest = {
  glyph_archive: {
    id: 'world-glyph-archive-v1',
    label: 'Glyph Archive',
    url: '/assets/explore/glyph-archive-v1.webp',
  },
  civic_nexus: {
    id: 'world-civic-nexus-v1',
    label: 'Civic Nexus',
    url: '/assets/explore/civic-nexus-v1.webp',
  },
  verdant_waypoint: {
    id: 'world-verdant-waypoint-v1',
    label: 'Verdant Waypoint',
    url: '/assets/explore/verdant-waypoint-v1.webp',
  },
  water_gate: {
    id: 'world-water-gate-v1',
    label: 'Water Gate',
    url: '/assets/explore/water-gate-v1.webp',
  },
  celestial_observatory: {
    id: 'world-celestial-observatory-v1',
    label: 'Celestial Observatory',
    url: '/assets/explore/celestial-observatory-v1.webp',
  },
  market_exchange: {
    id: 'world-market-exchange-v1',
    label: 'Market Exchange',
    url: '/assets/explore/market-exchange-v1.webp',
  },
} as const;

export const worldNodeArtProvenance = {
  generator: 'openai_builtin_imagegen_v1',
  reviewStatus: 'needs_cultural_review',
  projection: 'orthographic-three-quarter',
  lighting: 'upper-left-warm',
  sourceSize: [1254, 1254],
  runtimeSize: [512, 512],
} as const;

export interface ConstructionMaterialState {
  id: string;
  stage: ConstructionStageId;
  materialTreatment:
    | 'staked_earth'
    | 'set_stone_foundation'
    | 'rising_masonry_and_timber'
    | 'finished_materials_uninhabited'
    | 'living_materials';
  structureFraction: number;
  scaffoldDensity: number;
  materialPileCount: number;
  workerCount: number;
  ornamentFraction: number;
  activationLevel: number;
}

export type ConstructionMaterialStates = readonly [
  ConstructionMaterialState,
  ConstructionMaterialState,
  ConstructionMaterialState,
  ConstructionMaterialState,
  ConstructionMaterialState,
];

export interface ProceduralBuildingAsset {
  id: string;
  type: BuildingType;
  silhouette: 'temple' | 'courtyard' | 'garden' | 'workshop' | 'archive' | 'dock';
  wallMaterial: 'pale_stone' | 'lime_plaster' | 'timber' | 'obsidian_inlay';
  roofMaterial: 'none' | 'thatch' | 'flat_stone' | 'woven_canopy';
  activity:
    | 'solar'
    | 'residential'
    | 'farm'
    | 'masonry'
    | 'timber'
    | 'obsidian'
    | 'archive'
    | 'market'
    | 'harbor'
    | 'garden';
  footprint: readonly [number, number];
  anchor: readonly [number, number];
  tierScale: readonly [number, number, number, number];
  accent: 'jade' | 'gold' | 'maize' | 'obsidian' | 'water';
  constructionStates: ConstructionMaterialStates;
  branchOverlayIds: readonly BranchOverlayId[];
  productionGeometry: 'runtime_layered';
  reviewStatus: CulturalReviewStatus;
  provenance: 'procedural_runtime_v2';
}

export type BranchOverlayId = 'waterworks' | 'archive' | 'garden' | 'caravan' | 'artisan' | 'civic';

export interface BranchOverlayAsset {
  id: BranchOverlayId;
  buildingTypes: readonly BuildingType[];
  silhouetteLayer:
    'water_channel' | 'archive_inlay' | 'garden_bloom' | 'canopy' | 'craft' | 'banner';
  props: readonly string[];
  reviewStatus: CulturalReviewStatus;
  productionGeometry: 'runtime_layered';
}

export interface DistrictOverlayAsset {
  id:
    | 'lakeside_outpost'
    | 'garden_quarter'
    | 'artisan_quarter'
    | 'market_quarter'
    | 'ceremonial_quarter';
  order: 0 | 1 | 2 | 3 | 4;
  terrainTreatment: string;
  waterTreatment: string;
  silhouetteTreatment: string;
  reviewStatus: CulturalReviewStatus;
  productionGeometry: 'runtime_layered';
}

export interface CapabilityOverlayAsset {
  code: string;
  cue: 'solar' | 'water' | 'knowledge' | 'route' | 'craft' | 'civic' | 'garden';
  reviewStatus: CulturalReviewStatus;
  productionGeometry: 'runtime_layered';
}

export interface TribeProjectVisualAsset {
  key: string;
  projectCode: 'great_civic_complex' | 'canal_district' | 'celestial_observatory';
  phase: 1 | 2 | 3 | 4 | 5;
  stage: 'terraces' | 'core' | 'plaza' | 'gardens' | 'opening';
  palette: 'civic_gold' | 'water_jade' | 'celestial_obsidian';
  layers: {
    foundation: number;
    structure: number;
    scaffold: number;
    ornament: number;
    water: number;
    garden: number;
    population: number;
    activation: number;
  };
  reviewStatus: CulturalReviewStatus;
  provenance: 'procedural_runtime_v1';
  productionGeometry: 'runtime_layered';
}

export const runtimeVisualPolicy = {
  debugGeometryDefault: false,
  proxyGeometryAllowedInProduction: false,
  unknownTribeKeyBehavior: 'allowlisted_fallback',
} as const;

function constructionStatesFor(type: BuildingType): ConstructionMaterialStates {
  return [
    {
      id: `${type}_survey`,
      stage: 'survey',
      materialTreatment: 'staked_earth',
      structureFraction: 0,
      scaffoldDensity: 0,
      materialPileCount: 2,
      workerCount: 1,
      ornamentFraction: 0,
      activationLevel: 0,
    },
    {
      id: `${type}_foundation`,
      stage: 'foundation',
      materialTreatment: 'set_stone_foundation',
      structureFraction: 0.14,
      scaffoldDensity: 0.18,
      materialPileCount: 4,
      workerCount: 2,
      ornamentFraction: 0,
      activationLevel: 0,
    },
    {
      id: `${type}_structure`,
      stage: 'structure',
      materialTreatment: 'rising_masonry_and_timber',
      structureFraction: 0.58,
      scaffoldDensity: 1,
      materialPileCount: 3,
      workerCount: 3,
      ornamentFraction: 0,
      activationLevel: 0,
    },
    {
      id: `${type}_finishing`,
      stage: 'finishing',
      materialTreatment: 'finished_materials_uninhabited',
      structureFraction: 0.92,
      scaffoldDensity: 0.38,
      materialPileCount: 1,
      workerCount: 2,
      ornamentFraction: 0.72,
      activationLevel: 0,
    },
    {
      id: `${type}_activation`,
      stage: 'activation',
      materialTreatment: 'living_materials',
      structureFraction: 1,
      scaffoldDensity: 0,
      materialPileCount: 0,
      workerCount: 0,
      ornamentFraction: 1,
      activationLevel: 1,
    },
  ];
}

export const branchOverlayManifest = {
  waterworks: {
    id: 'waterworks',
    buildingTypes: ['solar_shrine'],
    silhouetteLayer: 'water_channel',
    props: ['sluice', 'reflecting_pool'],
    reviewStatus: 'needs_cultural_review',
    productionGeometry: 'runtime_layered',
  },
  archive: {
    id: 'archive',
    buildingTypes: ['solar_shrine'],
    silhouetteLayer: 'archive_inlay',
    props: ['jade_tablet', 'study_court'],
    reviewStatus: 'needs_cultural_review',
    productionGeometry: 'runtime_layered',
  },
  garden: {
    id: 'garden',
    buildingTypes: ['solar_shrine'],
    silhouetteLayer: 'garden_bloom',
    props: ['flower_terrace', 'shade_planter'],
    reviewStatus: 'needs_cultural_review',
    productionGeometry: 'runtime_layered',
  },
  caravan: {
    id: 'caravan',
    buildingTypes: ['market'],
    silhouetteLayer: 'canopy',
    props: ['route_awning', 'wayfinding_post'],
    reviewStatus: 'needs_cultural_review',
    productionGeometry: 'runtime_layered',
  },
  artisan: {
    id: 'artisan',
    buildingTypes: ['market'],
    silhouetteLayer: 'craft',
    props: ['craft_stall', 'obsidian_display'],
    reviewStatus: 'needs_cultural_review',
    productionGeometry: 'runtime_layered',
  },
  civic: {
    id: 'civic',
    buildingTypes: ['market'],
    silhouetteLayer: 'banner',
    props: ['civic_banner', 'public_planter'],
    reviewStatus: 'needs_cultural_review',
    productionGeometry: 'runtime_layered',
  },
} as const satisfies Record<BranchOverlayId, BranchOverlayAsset>;

export function resolveBranchOverlay(
  buildingType: BuildingType,
  branchCode?: string,
): BranchOverlayAsset | undefined {
  if (!branchCode || !Object.hasOwn(branchOverlayManifest, branchCode)) return undefined;
  const overlay: BranchOverlayAsset = branchOverlayManifest[branchCode as BranchOverlayId];
  return overlay.buildingTypes.includes(buildingType) ? overlay : undefined;
}

export const districtOverlayManifest = {
  lakeside_outpost: {
    id: 'lakeside_outpost',
    order: 0,
    terrainTreatment: 'raw_lakeside_terrace',
    waterTreatment: 'open_lake_edge',
    silhouetteTreatment: 'low_outpost',
    reviewStatus: 'needs_cultural_review',
    productionGeometry: 'runtime_layered',
  },
  garden_quarter: {
    id: 'garden_quarter',
    order: 1,
    terrainTreatment: 'cultivated_islands',
    waterTreatment: 'irrigation_paths',
    silhouetteTreatment: 'garden_canopies',
    reviewStatus: 'needs_cultural_review',
    productionGeometry: 'runtime_layered',
  },
  artisan_quarter: {
    id: 'artisan_quarter',
    order: 2,
    terrainTreatment: 'workshop_terraces',
    waterTreatment: 'working_canal',
    silhouetteTreatment: 'workshop_roofs',
    reviewStatus: 'needs_cultural_review',
    productionGeometry: 'runtime_layered',
  },
  market_quarter: {
    id: 'market_quarter',
    order: 3,
    terrainTreatment: 'paved_market_islands',
    waterTreatment: 'causeway_channels',
    silhouetteTreatment: 'market_awnings',
    reviewStatus: 'needs_cultural_review',
    productionGeometry: 'runtime_layered',
  },
  ceremonial_quarter: {
    id: 'ceremonial_quarter',
    order: 4,
    terrainTreatment: 'monumental_civic_stone',
    waterTreatment: 'cascades_and_pools',
    silhouetteTreatment: 'civic_landmark',
    reviewStatus: 'needs_cultural_review',
    productionGeometry: 'runtime_layered',
  },
} as const satisfies Record<string, DistrictOverlayAsset>;

export function resolveDistrictOverlay(code: string): DistrictOverlayAsset | undefined {
  if (!Object.hasOwn(districtOverlayManifest, code)) return undefined;
  return districtOverlayManifest[code as keyof typeof districtOverlayManifest];
}

export const capabilityOverlayManifest = {
  seasonal_ritual_discoveries: { cue: 'solar' },
  complex_fragment_analysis: { cue: 'knowledge' },
  merchant_route_contracts: { cue: 'route' },
  expedition_chain_capacity: { cue: 'water' },
  exploration_tool_crafting: { cue: 'craft' },
  civic_event_discoveries: { cue: 'civic' },
  long_route_contracts: { cue: 'route' },
  artisan_commissions: { cue: 'craft' },
  civic_supply_calls: { cue: 'civic' },
  waterway_discoveries: { cue: 'water' },
  archive_discoveries: { cue: 'knowledge' },
  garden_discoveries: { cue: 'garden' },
} as const satisfies Record<string, Pick<CapabilityOverlayAsset, 'cue'>>;

export function resolveCapabilityOverlay(code: string): CapabilityOverlayAsset | undefined {
  if (!Object.hasOwn(capabilityOverlayManifest, code)) return undefined;
  return {
    code,
    cue: capabilityOverlayManifest[code as keyof typeof capabilityOverlayManifest].cue,
    reviewStatus: 'needs_cultural_review',
    productionGeometry: 'runtime_layered',
  };
}

const tribePhaseLayers = {
  1: {
    foundation: 1,
    structure: 0.18,
    scaffold: 0.42,
    ornament: 0,
    water: 0.08,
    garden: 0,
    population: 0.12,
    activation: 0,
  },
  2: {
    foundation: 1,
    structure: 0.42,
    scaffold: 0.86,
    ornament: 0.08,
    water: 0.16,
    garden: 0.05,
    population: 0.28,
    activation: 0,
  },
  3: {
    foundation: 1,
    structure: 0.7,
    scaffold: 1,
    ornament: 0.34,
    water: 0.34,
    garden: 0.18,
    population: 0.48,
    activation: 0,
  },
  4: {
    foundation: 1,
    structure: 0.92,
    scaffold: 0.32,
    ornament: 0.82,
    water: 0.84,
    garden: 0.9,
    population: 0.72,
    activation: 0.14,
  },
  5: {
    foundation: 1,
    structure: 1,
    scaffold: 0,
    ornament: 1,
    water: 1,
    garden: 1,
    population: 1,
    activation: 1,
  },
} as const;

const tribePhaseStages = {
  1: 'terraces',
  2: 'core',
  3: 'plaza',
  4: 'gardens',
  5: 'opening',
} as const;

function tribeProjectVisual(
  projectCode: TribeProjectVisualAsset['projectCode'],
  phase: TribeProjectVisualAsset['phase'],
  palette: TribeProjectVisualAsset['palette'],
): TribeProjectVisualAsset {
  return {
    key: `tribe_${projectCode}_phase_${phase}`,
    projectCode,
    phase,
    stage: tribePhaseStages[phase],
    palette,
    layers: tribePhaseLayers[phase],
    reviewStatus: 'needs_cultural_review',
    provenance: 'procedural_runtime_v1',
    productionGeometry: 'runtime_layered',
  };
}

export const tribeProjectVisualManifest = {
  tribe_great_civic_complex_phase_1: tribeProjectVisual('great_civic_complex', 1, 'civic_gold'),
  tribe_great_civic_complex_phase_2: tribeProjectVisual('great_civic_complex', 2, 'civic_gold'),
  tribe_great_civic_complex_phase_3: tribeProjectVisual('great_civic_complex', 3, 'civic_gold'),
  tribe_great_civic_complex_phase_4: tribeProjectVisual('great_civic_complex', 4, 'civic_gold'),
  tribe_great_civic_complex_phase_5: tribeProjectVisual('great_civic_complex', 5, 'civic_gold'),
  tribe_canal_district_phase_1: tribeProjectVisual('canal_district', 1, 'water_jade'),
  tribe_canal_district_phase_2: tribeProjectVisual('canal_district', 2, 'water_jade'),
  tribe_canal_district_phase_3: tribeProjectVisual('canal_district', 3, 'water_jade'),
  tribe_canal_district_phase_4: tribeProjectVisual('canal_district', 4, 'water_jade'),
  tribe_canal_district_phase_5: tribeProjectVisual('canal_district', 5, 'water_jade'),
  tribe_celestial_observatory_phase_1: tribeProjectVisual(
    'celestial_observatory',
    1,
    'celestial_obsidian',
  ),
  tribe_celestial_observatory_phase_2: tribeProjectVisual(
    'celestial_observatory',
    2,
    'celestial_obsidian',
  ),
  tribe_celestial_observatory_phase_3: tribeProjectVisual(
    'celestial_observatory',
    3,
    'celestial_obsidian',
  ),
  tribe_celestial_observatory_phase_4: tribeProjectVisual(
    'celestial_observatory',
    4,
    'celestial_obsidian',
  ),
  tribe_celestial_observatory_phase_5: tribeProjectVisual(
    'celestial_observatory',
    5,
    'celestial_obsidian',
  ),
} as const satisfies Record<string, TribeProjectVisualAsset>;

export const defaultTribeProjectVisualKey = 'tribe_great_civic_complex_phase_1';

export function resolveTribeProjectVisual(key?: string): TribeProjectVisualAsset {
  if (key && Object.hasOwn(tribeProjectVisualManifest, key)) {
    return tribeProjectVisualManifest[key as keyof typeof tribeProjectVisualManifest];
  }
  return tribeProjectVisualManifest[defaultTribeProjectVisualKey];
}

export function resolveTribeProjectVisualForPhase(
  key: string | undefined,
  authoritativePhase: number | undefined,
): TribeProjectVisualAsset {
  const requested = resolveTribeProjectVisual(key);
  if (
    authoritativePhase === undefined ||
    !Number.isInteger(authoritativePhase) ||
    authoritativePhase < 1 ||
    authoritativePhase > 5
  ) {
    return requested;
  }
  const phaseKey = `tribe_${requested.projectCode}_phase_${authoritativePhase}`;
  return (
    tribeProjectVisualManifest[phaseKey as keyof typeof tribeProjectVisualManifest] ?? requested
  );
}

export interface EnvironmentAsset {
  id: string;
  url: string;
  /** Retained original when a byte-addressed lossless transport variant is used. */
  sourceUrl?: string;
  reviewStatus: CulturalReviewStatus;
  provenance: 'openai_builtin_imagegen_v1';
  role: 'phaser_environment_layer' | 'maplibre_image_source';
  cameraProfile?: typeof SETTLEMENT_CAMERA_PROFILE.id;
}

export interface GeneratedRuntimeAsset {
  id: string;
  url: string;
  reviewStatus: CulturalReviewStatus;
  provenance: 'openai_builtin_imagegen_v1';
  role:
    | 'player_portrait'
    | 'codex_lore_plate'
    | 'tribe_project_layer'
    | 'decode_tablet_layer'
    | 'decode_turning_stone'
    | 'decode_material_surface'
    | 'decode_glyph_relief';
}

export interface SettlementBuildingSpriteAsset {
  id: string;
  url: string;
  buildingTypes: readonly BuildingType[];
  stage: 'completed' | 'structure';
  displayWidthFactor: number;
  /** Inclusive server building-level band; omitted for shared family art. */
  levels?: readonly [number, number];
  /** Retained authoring source when runtime delivery uses a lossless derivative. */
  sourceUrl?: string;
  reviewStatus: CulturalReviewStatus;
  provenance: 'openai_builtin_imagegen_v1';
  role: 'settlement_building_layer';
  cameraProfile: typeof SETTLEMENT_CAMERA_PROFILE.id;
}

export interface SettlementGroundSpriteAsset {
  id: string;
  url: string;
  reviewStatus: CulturalReviewStatus;
  provenance: 'openai_builtin_imagegen_v1';
  role: 'settlement_ground_layer';
  cameraProfile: typeof SETTLEMENT_CAMERA_PROFILE.id;
  groundAnchorY: number;
}

export const environmentAssetManifest = {
  settlement: {
    id: 'environment_settlement_basin_v1',
    url: homeRuntimeArt['settlement-basin-v1'].url,
    sourceUrl: homeRuntimeArt['settlement-basin-v1'].sourceUrl,
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'phaser_environment_layer',
    cameraProfile: SETTLEMENT_CAMERA_PROFILE.id,
  },
  explore: {
    id: 'environment_explore_archipelago_v1',
    url: '/assets/world/explore-archipelago-v1.webp',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'maplibre_image_source',
  },
  decode: {
    id: 'environment_decode_sanctuary_v1',
    url: '/assets/world/decode-sanctuary-v1.webp',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'phaser_environment_layer',
  },
} as const satisfies Record<string, EnvironmentAsset>;

export const generatedRuntimeAssetManifest = {
  decodeGlyphReliefs: {
    id: 'decode_glyph_reliefs_v1',
    url: '/assets/decode/glyph-reliefs-v1.webp',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'decode_glyph_relief',
  },
  decodeLimestoneSurface: {
    id: 'decode_limestone_surface_v1',
    url: '/assets/decode/limestone-surface-v1.webp',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'decode_material_surface',
  },
  decodeObsidianSurface: {
    id: 'decode_obsidian_surface_v1',
    url: '/assets/decode/obsidian-surface-v1.webp',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'decode_material_surface',
  },
  decodeSolarRelief: {
    id: 'decode_solar_relief_v1',
    url: '/assets/decode/solar-relief-v1.webp',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'decode_glyph_relief',
  },
  decodeTabletFoundation: {
    id: 'decode_tablet_foundation_v1',
    url: '/assets/world/decode-tablet-foundation-v1.png',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'decode_tablet_layer',
  },
  decodeTurningStoneLimestone: {
    id: 'decode_turning_stone_limestone_v2',
    url: '/assets/decode/turning-stone-limestone-v2.png',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'decode_turning_stone',
  },
  decodeTurningStoneObsidian: {
    id: 'decode_turning_stone_obsidian_v2',
    url: '/assets/decode/turning-stone-obsidian-v2.png',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'decode_turning_stone',
  },
  wayfinderPortrait: {
    id: 'portrait_wayfinder_citlali_v1',
    url: '/assets/portraits/wayfinder-citlali-v1.png',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'player_portrait',
  },
  canalKeeperPortrait: {
    id: 'portrait_canal_keeper_v1',
    url: '/assets/portraits/canal-keeper-v1.webp',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'player_portrait',
  },
  obsidianArtisanPortrait: {
    id: 'portrait_obsidian_artisan_v1',
    url: '/assets/portraits/obsidian-artisan-v1.webp',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'player_portrait',
  },
  gardenStewardPortrait: {
    id: 'portrait_garden_steward_v1',
    url: '/assets/portraits/garden-steward-v1.webp',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'player_portrait',
  },
  codexKeeperPortrait: {
    id: 'portrait_codex_keeper_v1',
    url: '/assets/portraits/codex-keeper-v1.webp',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'player_portrait',
  },
  skyObserverPortrait: {
    id: 'portrait_sky_observer_v1',
    url: '/assets/portraits/sky-observer-v1.webp',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'player_portrait',
  },
  sunFamilyChronicle: {
    id: 'codex_sun_family_chronicle_v1',
    url: '/assets/codex/sun-family-chronicle-v1.jpg',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'codex_lore_plate',
  },
  jaguarFamilyRelief: {
    id: 'codex_jaguar_family_relief_v1',
    url: '/assets/codex/jaguar-family-relief-v1.jpg',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'codex_lore_plate',
  },
  rainFamilyRelief: {
    id: 'codex_rain_family_relief_v1',
    url: '/assets/codex/rain-family-relief-v1.webp',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'codex_lore_plate',
  },
  maizeFamilyRelief: {
    id: 'codex_maize_family_relief_v1',
    url: '/assets/codex/maize-family-relief-v1.webp',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'codex_lore_plate',
  },
  waterFamilyRelief: {
    id: 'codex_water_family_relief_v1',
    url: '/assets/codex/water-family-relief-v1.webp',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'codex_lore_plate',
  },
  serpentFamilyRelief: {
    id: 'codex_serpent_family_relief_v1',
    url: '/assets/codex/serpent-family-relief-v1.webp',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'codex_lore_plate',
  },
  sunFamilyRelief: {
    id: 'codex_sun_family_relief_v1',
    url: '/assets/codex/sun-family-relief-v1.webp',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'codex_lore_plate',
  },
  greatCivicComplexPhaseOne: {
    id: 'tribe_great_civic_complex_phase_1_v1',
    url: '/assets/tribe/great-civic-complex-phase-1-v1.jpg',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'tribe_project_layer',
  },
  greatCivicComplexPhaseTwo: {
    id: 'tribe_great_civic_complex_phase_2_v1',
    url: '/assets/tribe/great-civic-complex-phase-2-v1.jpg',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'tribe_project_layer',
  },
  greatCivicComplexPhaseThree: {
    id: 'tribe_great_civic_complex_phase_3_v1',
    url: '/assets/tribe/great-civic-complex-phase-3-v1.jpg',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'tribe_project_layer',
  },
  greatCivicComplexPhaseFour: {
    id: 'tribe_great_civic_complex_phase_4_v1',
    url: '/assets/tribe/great-civic-complex-phase-4-v1.jpg',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'tribe_project_layer',
  },
  greatCivicComplexPhaseFive: {
    id: 'tribe_great_civic_complex_phase_5_v1',
    url: '/assets/tribe/great-civic-complex-phase-5-v1.jpg',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'tribe_project_layer',
  },
} as const satisfies Record<string, GeneratedRuntimeAsset>;

const greatCivicComplexLayerByPhase = {
  1: generatedRuntimeAssetManifest.greatCivicComplexPhaseOne,
  2: generatedRuntimeAssetManifest.greatCivicComplexPhaseTwo,
  3: generatedRuntimeAssetManifest.greatCivicComplexPhaseThree,
  4: generatedRuntimeAssetManifest.greatCivicComplexPhaseFour,
  5: generatedRuntimeAssetManifest.greatCivicComplexPhaseFive,
} as const;

export function resolveTribeProjectLayer(
  projectCode: TribeProjectVisualAsset['projectCode'],
  phase: TribeProjectVisualAsset['phase'],
): GeneratedRuntimeAsset | undefined {
  if (projectCode !== 'great_civic_complex') return undefined;
  return greatCivicComplexLayerByPhase[phase];
}

/** Ground-only plane, never an upright sprite or an ambient/unowned plot. */
export const settlementWaterFoundationManifest = {
  id: 'settlement_water_foundation_v1',
  url: homeRuntimeArt['water-foundation-v1'].url,
  sourceUrl: homeRuntimeArt['water-foundation-v1'].sourceUrl,
  provenance: 'openai_builtin_imagegen_v1',
  reviewStatus: 'needs_cultural_review',
  role: 'owned_building_ground_foundation',
  buildingTypes: ['dock', 'chinampa_farm', 'market'],
  sourceWidth: 1536,
  sourceHeight: 1024,
  // Inspected outer ground-contact corners in source pixels: back, right, front, left.
  // Clip to this plane; image padding is not part of the authoritative building plot.
  sourceQuad: [
    { x: 580, y: 104 },
    { x: 1440, y: 582 },
    { x: 958, y: 918 },
    { x: 72, y: 414 },
  ],
  projection: 'source_quad_to_authoritative_projected_footprint',
} as const;

export const settlementGroundSpriteManifest = {
  chinampaPlot: {
    id: 'settlement_chinampa_plot_v1',
    url: '/assets/settlement/chinampa-plot-v1.png',
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'settlement_ground_layer',
    cameraProfile: SETTLEMENT_CAMERA_PROFILE.id,
    groundAnchorY: 0.91,
  },
} as const satisfies Record<string, SettlementGroundSpriteAsset>;

/** Authored runtime layers, never flattened settlement scenes. */
export const settlementDetailSpriteManifest = {
  banner: {
    id: 'settlement_civic_banner_v1',
    url: '/assets/settlement/civic-banner-v1.png',
    role: 'ambient_banner',
    groundAnchorY: 0.96,
  },
  planter: {
    id: 'settlement_tropical_planter_v1',
    url: '/assets/settlement/tropical-planter-v1.png',
    role: 'ambient_vegetation',
    groundAnchorY: 0.99,
  },
  marketStall: {
    id: 'settlement_market_stall_v1',
    url: '/assets/settlement/market-stall-v1.png',
    role: 'ambient_market_stall',
    groundAnchorY: 0.97,
  },
  survey: {
    id: 'settlement_survey_ground_v1',
    url: '/assets/settlement/survey-ground-v1.png',
    role: 'construction_survey',
    groundAnchorY: 0.855,
  },
  plantingBeds: {
    id: 'settlement_planting_beds_v1',
    url: '/assets/settlement/planting-beds-v1.png',
    role: 'construction_planting_beds',
    groundAnchorY: 0.91,
  },
  dockStructure: {
    id: 'settlement_dock_construction_v1',
    url: '/assets/settlement/dock-construction-v1.png',
    role: 'construction_dock',
    groundAnchorY: 0.91,
  },
  foundation: {
    id: 'settlement_foundation_pad_v1',
    url: '/assets/settlement/foundation-pad-v1.png',
    role: 'construction_foundation',
    groundAnchorY: 0.91,
  },
  scaffold: {
    id: 'settlement_construction_scaffold_v1',
    url: '/assets/settlement/construction-scaffold-v1.png',
    role: 'construction_structure',
    groundAnchorY: 0.96,
  },
  finishingScaffold: {
    id: 'settlement_finishing_scaffold_v1',
    url: homeRuntimeArt['finishing-scaffold-v1'].url,
    sourceUrl: homeRuntimeArt['finishing-scaffold-v1'].sourceUrl,
    role: 'construction_finishing_accessory',
    groundAnchorY: 0.946,
  },
  canoe: {
    id: 'settlement_canal_canoe_v1',
    url: '/assets/settlement/canal-canoe-v1.png',
    role: 'ambient_canoe',
    groundAnchorY: 0.65,
  },
  resident: {
    id: 'settlement_resident_poses_v1',
    url: '/assets/settlement/resident-poses-v1.png',
    role: 'ambient_resident',
    groundAnchorY: 0.98,
    frameWidth: 256,
    frameHeight: 256,
    // The generated poses did not pass walk-cycle QA. Never fake a walk animation.
    staticFrame: 0,
  },
} as const;

export const settlementDetailArtPolicy = {
  cameraProfile: SETTLEMENT_CAMERA_PROFILE.id,
  provenance: 'openai_builtin_imagegen_v1',
  reviewStatus: 'needs_cultural_review',
  residentAnimationAccepted: false,
} as const;

export const settlementBuildingSpriteManifest: readonly SettlementBuildingSpriteAsset[] = [
  {
    id: 'settlement_codex_house_completed_v1',
    url: '/assets/settlement/codex-house-v1.png',
    buildingTypes: ['codex_house'],
    stage: 'completed',
    displayWidthFactor: 1.3,
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'settlement_building_layer',
    cameraProfile: SETTLEMENT_CAMERA_PROFILE.id,
  },
  {
    id: 'settlement_stone_yard_completed_v1',
    url: '/assets/settlement/stone-yard-v1.png',
    buildingTypes: ['stone_yard'],
    stage: 'completed',
    displayWidthFactor: 1.3,
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'settlement_building_layer',
    cameraProfile: SETTLEMENT_CAMERA_PROFILE.id,
  },
  {
    id: 'settlement_timber_workshop_completed_v1',
    url: '/assets/settlement/timber-workshop-v1.png',
    buildingTypes: ['timber_workshop'],
    stage: 'completed',
    displayWidthFactor: 1.3,
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'settlement_building_layer',
    cameraProfile: SETTLEMENT_CAMERA_PROFILE.id,
  },
  {
    id: 'settlement_garden_completed_v1',
    url: '/assets/settlement/garden-plaza-v1.png',
    buildingTypes: ['garden'],
    stage: 'completed',
    displayWidthFactor: 1.3,
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'settlement_building_layer',
    cameraProfile: SETTLEMENT_CAMERA_PROFILE.id,
  },
  {
    id: 'settlement_dock_completed_v1',
    url: '/assets/settlement/canoe-dock-v1.png',
    buildingTypes: ['dock'],
    stage: 'completed',
    displayWidthFactor: 1.3,
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'settlement_building_layer',
    cameraProfile: SETTLEMENT_CAMERA_PROFILE.id,
  },
  {
    id: 'settlement_solar_shrine_completed_v1',
    url: '/assets/settlement/solar-shrine-t3-v1.png',
    buildingTypes: ['solar_shrine'],
    stage: 'completed',
    levels: [1, 3],
    displayWidthFactor: 1.55,
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'settlement_building_layer',
    cameraProfile: SETTLEMENT_CAMERA_PROFILE.id,
  },
  {
    id: 'settlement_solar_shrine_monument_v2',
    url: homeRuntimeArt['solar-shrine-monument-v2'].url,
    sourceUrl: homeRuntimeArt['solar-shrine-monument-v2'].sourceUrl,
    buildingTypes: ['solar_shrine'],
    stage: 'completed',
    levels: [4, 5],
    displayWidthFactor: 1.55,
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'settlement_building_layer',
    cameraProfile: SETTLEMENT_CAMERA_PROFILE.id,
  },
  {
    id: 'settlement_solar_shrine_structure_v1',
    url: '/assets/settlement/solar-shrine-structure-v1.png',
    buildingTypes: ['solar_shrine'],
    stage: 'structure',
    displayWidthFactor: 1.5,
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'settlement_building_layer',
    cameraProfile: SETTLEMENT_CAMERA_PROFILE.id,
  },
  {
    id: 'settlement_home_thatch_v2',
    url: homeRuntimeArt['home-thatch-v2'].url,
    sourceUrl: homeRuntimeArt['home-thatch-v2'].sourceUrl,
    buildingTypes: ['homes'],
    stage: 'completed',
    levels: [1, 2],
    displayWidthFactor: 1.35,
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'settlement_building_layer',
    cameraProfile: SETTLEMENT_CAMERA_PROFILE.id,
  },
  {
    id: 'settlement_home_terrace_v2',
    url: homeRuntimeArt['home-terrace-v2'].url,
    sourceUrl: homeRuntimeArt['home-terrace-v2'].sourceUrl,
    buildingTypes: ['homes'],
    stage: 'completed',
    levels: [3, 4],
    displayWidthFactor: 1.35,
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'settlement_building_layer',
    cameraProfile: SETTLEMENT_CAMERA_PROFILE.id,
  },
  {
    id: 'settlement_home_residence_v2',
    url: homeRuntimeArt['home-residence-v2'].url,
    sourceUrl: homeRuntimeArt['home-residence-v2'].sourceUrl,
    buildingTypes: ['homes'],
    stage: 'completed',
    levels: [5, 5],
    displayWidthFactor: 1.35,
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'settlement_building_layer',
    cameraProfile: SETTLEMENT_CAMERA_PROFILE.id,
  },
  {
    id: 'settlement_chinampa_farm_completed_v1',
    url: '/assets/settlement/chinampa-farm-t3-v1.png',
    buildingTypes: ['chinampa_farm'],
    stage: 'completed',
    displayWidthFactor: 1.25,
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'settlement_building_layer',
    cameraProfile: SETTLEMENT_CAMERA_PROFILE.id,
  },
  {
    id: 'settlement_obsidian_workshop_completed_v1',
    url: '/assets/settlement/obsidian-workshop-t3-v1.png',
    buildingTypes: ['obsidian_workshop'],
    stage: 'completed',
    displayWidthFactor: 1.25,
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'settlement_building_layer',
    cameraProfile: SETTLEMENT_CAMERA_PROFILE.id,
  },
  {
    id: 'settlement_canal_market_dock_completed_v1',
    url: '/assets/settlement/canal-market-dock-t3-v1.png',
    buildingTypes: ['market'],
    stage: 'completed',
    displayWidthFactor: 1.3,
    reviewStatus: 'needs_cultural_review',
    provenance: 'openai_builtin_imagegen_v1',
    role: 'settlement_building_layer',
    cameraProfile: SETTLEMENT_CAMERA_PROFILE.id,
  },
];

export function resolveSettlementBuildingSprite(
  buildingType: BuildingType,
  stage: SettlementBuildingSpriteAsset['stage'],
  level = 1,
  orientation = 0,
): SettlementBuildingSpriteAsset | undefined {
  const base = settlementBuildingSpriteManifest.find(
    (asset) =>
      asset.stage === stage &&
      asset.buildingTypes.includes(buildingType) &&
      (!asset.levels || (level >= asset.levels[0] && level <= asset.levels[1])),
  );
  if (!base || orientation === 0) return base;
  const original = settlementBuildingRegistration(buildingType, level, stage);
  const directional = settlementBuildingRegistration(buildingType, level, stage, orientation);
  if (directional.source === original.source) return base;
  return {
    ...base,
    id: `${base.id}_r${orientation}`,
    url: `/assets/settlement/${directional.source}`,
    sourceUrl: `/assets/settlement/${directional.source}`,
  };
}

/** Authored elongated worksites. Effective server dimensions are already rotated. */
export const settlementLonghouseStructureDirections = [0, 1, 2, 3].map((orientation) => ({
  ...settlementDetailSpriteManifest.scaffold,
  id: `settlement_construction_longhouse_r${orientation}_v1`,
  url: `/assets/settlement/construction-longhouse-r${orientation}-v1.png`,
  sourceUrl: `/assets/settlement/construction-longhouse-r${orientation}-v1.png`,
}));

export function resolveSettlementLonghouseStructure(type: BuildingType, orientation = 0) {
  if (!['stone_yard', 'timber_workshop', 'codex_house', 'obsidian_workshop'].includes(type))
    return undefined;
  const turn =
    Number.isInteger(orientation) && orientation >= 0 && orientation < 4 ? orientation : 0;
  // Obsidian's base plot is 1x2, the other three are 2x1. Only offset the art lookup.
  return settlementLonghouseStructureDirections[
    (turn + (type === 'obsidian_workshop' ? 1 : 0)) % 4
  ];
}

/** Construction visuals follow server state; an unfinished farm must never become a house. */
export function resolveSettlementConstructionArt(
  type: BuildingType,
  stage: ConstructionStageId,
  level = 1,
  orientation = 0,
) {
  if (stage === 'activation' || stage === 'finishing') {
    const completed = resolveSettlementBuildingSprite(type, 'completed', level, orientation);
    return (
      completed && { ...completed, groundAnchorY: SETTLEMENT_CAMERA_PROFILE.spriteGroundAnchorY }
    );
  }
  if (stage === 'survey') return settlementDetailSpriteManifest.survey;
  if (stage === 'foundation') return settlementDetailSpriteManifest.foundation;
  if (type === 'chinampa_farm' || type === 'garden')
    return settlementDetailSpriteManifest.plantingBeds;
  if (type === 'dock') return settlementDetailSpriteManifest.dockStructure;
  const longhouse = resolveSettlementLonghouseStructure(type, orientation);
  if (longhouse) return longhouse;
  const structure = resolveSettlementBuildingSprite(type, 'structure', level, orientation);
  return structure
    ? { ...structure, groundAnchorY: SETTLEMENT_CAMERA_PROFILE.spriteGroundAnchorY }
    : settlementDetailSpriteManifest.scaffold;
}

/** Scaffolds remain physical until finishing ends; completed buildings never retain them. */
export const settlementFinishingScaffoldDirections = [
  settlementDetailSpriteManifest.finishingScaffold,
  {
    ...settlementDetailSpriteManifest.finishingScaffold,
    id: 'settlement_finishing_scaffold_r1_v1',
    url: homeRuntimeArt['finishing-scaffold-r1-v1'].url,
    sourceUrl: homeRuntimeArt['finishing-scaffold-r1-v1'].sourceUrl,
  },
  {
    ...settlementDetailSpriteManifest.finishingScaffold,
    id: 'settlement_finishing_scaffold_r2_v1',
    url: homeRuntimeArt['finishing-scaffold-r2-v1'].url,
    sourceUrl: homeRuntimeArt['finishing-scaffold-r2-v1'].sourceUrl,
  },
  {
    ...settlementDetailSpriteManifest.finishingScaffold,
    id: 'settlement_finishing_scaffold_r3_v1',
    url: homeRuntimeArt['finishing-scaffold-r3-v1'].url,
    sourceUrl: homeRuntimeArt['finishing-scaffold-r3-v1'].sourceUrl,
  },
] as const;

export function resolveSettlementConstructionAccessory(
  stage?: ConstructionStageId,
  orientation = 0,
) {
  return stage === 'finishing'
    ? (settlementFinishingScaffoldDirections[orientation] ??
        settlementFinishingScaffoldDirections[0])
    : undefined;
}

export const proceduralBuildingManifest: Record<BuildingType, ProceduralBuildingAsset> = {
  solar_shrine: {
    id: 'building_solar_shrine_procedural_v2',
    type: 'solar_shrine',
    silhouette: 'temple',
    wallMaterial: 'pale_stone',
    roofMaterial: 'flat_stone',
    activity: 'solar',
    footprint: [3, 3],
    anchor: [0.5, 0.88],
    // A civic monument remains substantial from first construction; later
    // progression comes from architecture as well as modest footprint growth.
    tierScale: [1.105, 1.17, 1.235, 1.3],
    accent: 'gold',
    constructionStates: constructionStatesFor('solar_shrine'),
    branchOverlayIds: ['waterworks', 'archive', 'garden'],
    productionGeometry: 'runtime_layered',
    reviewStatus: 'needs_cultural_review',
    provenance: 'procedural_runtime_v2',
  },
  homes: {
    id: 'building_homes_procedural_v2',
    type: 'homes',
    silhouette: 'courtyard',
    wallMaterial: 'lime_plaster',
    roofMaterial: 'thatch',
    activity: 'residential',
    footprint: [2, 2],
    anchor: [0.5, 0.82],
    tierScale: [0.85, 1, 1.12, 1.24],
    accent: 'jade',
    constructionStates: constructionStatesFor('homes'),
    branchOverlayIds: [],
    productionGeometry: 'runtime_layered',
    reviewStatus: 'needs_cultural_review',
    provenance: 'procedural_runtime_v2',
  },
  chinampa_farm: {
    id: 'building_chinampa_procedural_v2',
    type: 'chinampa_farm',
    silhouette: 'garden',
    wallMaterial: 'timber',
    roofMaterial: 'none',
    activity: 'farm',
    footprint: [3, 2],
    anchor: [0.5, 0.72],
    tierScale: [0.9, 1, 1.08, 1.18],
    accent: 'maize',
    constructionStates: constructionStatesFor('chinampa_farm'),
    branchOverlayIds: [],
    productionGeometry: 'runtime_layered',
    reviewStatus: 'needs_cultural_review',
    provenance: 'procedural_runtime_v2',
  },
  stone_yard: {
    id: 'building_stone_yard_procedural_v2',
    type: 'stone_yard',
    silhouette: 'workshop',
    wallMaterial: 'pale_stone',
    roofMaterial: 'flat_stone',
    activity: 'masonry',
    footprint: [2, 2],
    anchor: [0.5, 0.8],
    tierScale: [0.9, 1, 1.12, 1.2],
    accent: 'gold',
    constructionStates: constructionStatesFor('stone_yard'),
    branchOverlayIds: [],
    productionGeometry: 'runtime_layered',
    reviewStatus: 'needs_cultural_review',
    provenance: 'procedural_runtime_v2',
  },
  timber_workshop: {
    id: 'building_timber_workshop_procedural_v2',
    type: 'timber_workshop',
    silhouette: 'workshop',
    wallMaterial: 'timber',
    roofMaterial: 'thatch',
    activity: 'timber',
    footprint: [2, 2],
    anchor: [0.5, 0.8],
    tierScale: [0.9, 1, 1.12, 1.2],
    accent: 'maize',
    constructionStates: constructionStatesFor('timber_workshop'),
    branchOverlayIds: [],
    productionGeometry: 'runtime_layered',
    reviewStatus: 'needs_cultural_review',
    provenance: 'procedural_runtime_v2',
  },
  obsidian_workshop: {
    id: 'building_obsidian_workshop_procedural_v2',
    type: 'obsidian_workshop',
    silhouette: 'workshop',
    wallMaterial: 'obsidian_inlay',
    roofMaterial: 'thatch',
    activity: 'obsidian',
    footprint: [2, 2],
    anchor: [0.5, 0.82],
    tierScale: [0.9, 1, 1.12, 1.22],
    accent: 'obsidian',
    constructionStates: constructionStatesFor('obsidian_workshop'),
    branchOverlayIds: [],
    productionGeometry: 'runtime_layered',
    reviewStatus: 'needs_cultural_review',
    provenance: 'procedural_runtime_v2',
  },
  codex_house: {
    id: 'building_codex_house_procedural_v2',
    type: 'codex_house',
    silhouette: 'archive',
    wallMaterial: 'lime_plaster',
    roofMaterial: 'flat_stone',
    activity: 'archive',
    footprint: [2, 2],
    anchor: [0.5, 0.82],
    tierScale: [0.9, 1, 1.14, 1.25],
    accent: 'jade',
    constructionStates: constructionStatesFor('codex_house'),
    branchOverlayIds: [],
    productionGeometry: 'runtime_layered',
    reviewStatus: 'needs_cultural_review',
    provenance: 'procedural_runtime_v2',
  },
  market: {
    id: 'building_market_procedural_v2',
    type: 'market',
    silhouette: 'courtyard',
    wallMaterial: 'lime_plaster',
    roofMaterial: 'woven_canopy',
    activity: 'market',
    footprint: [3, 2],
    anchor: [0.5, 0.78],
    tierScale: [0.9, 1, 1.14, 1.26],
    accent: 'gold',
    constructionStates: constructionStatesFor('market'),
    branchOverlayIds: ['caravan', 'artisan', 'civic'],
    productionGeometry: 'runtime_layered',
    reviewStatus: 'needs_cultural_review',
    provenance: 'procedural_runtime_v2',
  },
  dock: {
    id: 'building_dock_procedural_v2',
    type: 'dock',
    silhouette: 'dock',
    wallMaterial: 'timber',
    roofMaterial: 'none',
    activity: 'harbor',
    footprint: [3, 1],
    anchor: [0.5, 0.72],
    tierScale: [0.9, 1, 1.08, 1.16],
    accent: 'water',
    constructionStates: constructionStatesFor('dock'),
    branchOverlayIds: [],
    productionGeometry: 'runtime_layered',
    reviewStatus: 'needs_cultural_review',
    provenance: 'procedural_runtime_v2',
  },
  garden: {
    id: 'building_garden_procedural_v2',
    type: 'garden',
    silhouette: 'garden',
    wallMaterial: 'pale_stone',
    roofMaterial: 'none',
    activity: 'garden',
    footprint: [2, 2],
    anchor: [0.5, 0.7],
    tierScale: [0.9, 1, 1.08, 1.18],
    accent: 'jade',
    constructionStates: constructionStatesFor('garden'),
    branchOverlayIds: [],
    productionGeometry: 'runtime_layered',
    reviewStatus: 'needs_cultural_review',
    provenance: 'procedural_runtime_v2',
  },
};
